import React, { useCallback, useEffect, useState } from 'react';

import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import EmailDistributionService from 'src/services/emailDistribution/emailDistributionService';
import PlacementDetailsService, { type InitiatePlacementTransfer } from 'src/services/placementDetailsService';
import { getAllActiveSites, type Site } from 'src/store/siteSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createSearchRegExp } from 'src/utils/searchUtils';

interface InitiateTransferModalProps {
  placementId: number | string;
  isOpen: boolean;
  closeModal: VoidFunction;
  managerAlias: string;
  fc: string;
  onTransferSuccess: VoidFunction;
}

const InitiateTransferModal = ({
  placementId,
  isOpen,
  closeModal,
  managerAlias,
  fc,
  onTransferSuccess,
}: InitiateTransferModalProps) => {
  const [step, setStep] = useState<'initiate' | 'confirm'>('initiate');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>();
  const [emailRecipients, setEmailRecipients] = useState('');
  const { activeSites, allowedSites } = useAppSelector((state) => state.sites);

  const searchRegExp = createSearchRegExp(searchQuery);
  const matchedSiteOptions = activeSites.filter((option) => !searchQuery || searchRegExp.test(option.site));
  const dispatch = useAppDispatch();

  const getEmailsForTransferPlacement = useCallback(
    async (destinationSiteId: number) => {
      const sourceSiteId = allowedSites.filter((site: Site) => site.site === fc)[0].id;
      const sourceSiteEmails = await EmailDistributionService.getForSite(sourceSiteId);
      const destinationSiteEmails = await EmailDistributionService.getForSite(destinationSiteId);
      const sourceSiteApprovedEmails = sourceSiteEmails.data?.placementEmailRecipients || '';
      const destinationSiteApprovedEmails = destinationSiteEmails.data?.placementEmailRecipients || '';
      const includeManager =
        sourceSiteEmails.data?.supervisorsForPlacementEmail ||
        destinationSiteEmails.data?.supervisorsForPlacementEmail ||
        false;
      let emails = `${sourceSiteApprovedEmails};${destinationSiteApprovedEmails}`;
      emails = includeManager ? `${emails};${managerAlias}@amazon.com` : emails;
      // Get rid of trailing/leading semi colon and any empty emails
      emails = emails
        .split(';')
        .map((email) => email.trim())
        .filter((email) => email.length > 0)
        .join(';');

      setEmailRecipients(emails);
    },
    [allowedSites, fc, managerAlias],
  );

  useEffect(() => {
    if (isOpen) {
      setStep('initiate');
      setSearchQuery('');
      setSelectedSiteId(undefined);
      setEmailRecipients('');
      dispatch(getAllActiveSites());
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (selectedSiteId) {
      getEmailsForTransferPlacement(selectedSiteId);
    }
  }, [dispatch, selectedSiteId, getEmailsForTransferPlacement]);

  const initiatePlacementTransfer = async () => {
    const sourceSiteId = allowedSites.filter((site: Site) => site.site === fc)[0].id;
    const payload: InitiatePlacementTransfer = {
      accommodationId: placementId,
      emailRecipients,
      originSiteId: sourceSiteId,
      destinationSiteId: selectedSiteId!,
    };
    const { status } = await PlacementDetailsService.initiatePlacementTransfer(payload);

    if (status === 200) {
      closeModal();
      onTransferSuccess();
    }
  };

  return (
    <>
      <Modal title="Initiate Transfer" open={isOpen && step === 'initiate'} onClose={closeModal} width={'416px'}>
        <Column>
          <Row>
            <Text>
              Please note that you are transferring ownership of the RTW placement to the site selected below. Once
              transferred, your site will no longer have access to this RTW placement.
            </Text>
          </Row>
          <Row widths={['fit', 'fill']}>
            <Text tag="label" type="b300" color="primary">
              Site
            </Text>
            <Select
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              value={selectedSiteId}
              placeholder="Select site to transfer placement to"
              onChange={(siteId) => setSelectedSiteId(siteId)}
            >
              {matchedSiteOptions.map((site) => (
                <SelectOption label={site.site} value={site.id} key={site.id}></SelectOption>
              ))}
            </Select>
          </Row>

          {selectedSiteId !== undefined && (
            <Column>
              <Text>
                Recipients populated below originate from the "Approved Onsite Placement" email settings at both sites
                involved in the transfer. You may add additional recipients if necessary.
              </Text>
              <Row widths={['grid-12']}>
                <Textarea
                  size="medium"
                  value={emailRecipients}
                  onChange={(val) => {
                    setEmailRecipients(val);
                  }}
                  label="Email recipients"
                  error={false}
                />
              </Row>
            </Column>
          )}

          <Row width="fill" alignmentHorizontal="end">
            <Button disabled={selectedSiteId === undefined} onClick={() => setStep('confirm')}>
              Transfer
            </Button>
          </Row>
        </Column>
      </Modal>

      <ConfirmModal
        modalTitle="Confirm Transfer?"
        modalDescription="Once transferred, your site will no longer have access to this RTW placement."
        open={isOpen && step === 'confirm'}
        setOpen={closeModal}
        onConfirm={initiatePlacementTransfer}
      />
    </>
  );
};

export default InitiateTransferModal;
